import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getPartnerCatalogApi, updatePartnerCatalogApi } from 'api/partner';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import useInfoDialog from 'common/hooks/useInfoDialog';
import { capitalizeName } from 'common/utils/titleCase';
import { ALERT_PARTNER_CHECKBOX } from 'modules/CatalogManagement/components/AlertPartnersCheckbox';
import CatalogDialogHeader from 'modules/CatalogManagement/components/CatalogDialogHeader';
import { formatDrugName, formatForm, formatPackSize } from 'modules/CatalogManagement/utils/format';
import { handleError } from 'store/thunks/errorHandlerThunks';

import AddPartnerCatalogItemDialog from './AddPartnerCatalogItemDialog';
import EditPartnerCatalogItemDialog from './EditPartnerCatalogItemDialog';

// Mirrors e-scribe-ui's PartnerFormulary + PartnerFormularyDetails (the
// SysAdmin "manage this partner's catalog" screen), rehomed as a dialog off
// the Partner Management actions menu rather than a tab of a partner-details
// page -- POM has no such page for this ticket to reuse.
const describeRow = (row) =>
  [row.strength, formatPackSize(row.packSizeValue, row.packSizeUnit), row.formAlias]
    .filter(Boolean)
    .join(' ');

const PartnerCatalogModal = ({ partner, showModal, handleClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { InfoDialog, showInfoDialog } = useInfoDialog();

  const [drugs, setDrugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCatalogId, setSelectedCatalogId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const loadPartnerCatalog = () => {
    setLoading(true);

    return getPartnerCatalogApi({ partnerId: partner.partnerUuid })
      .then((results) => setDrugs(results ?? []))
      .catch((error) => dispatch(handleError({ error })))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!showModal) return;

    setSearch('');
    setSelectedCatalogId(null);
    loadPartnerCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, partner.partnerUuid]);

  const allRows = useMemo(
    () =>
      drugs
        .flatMap(({ drugName, variants }) =>
          (variants ?? []).map((variant) => ({ drugName, ...variant }))
        )
        .sort((left, right) =>
          formatDrugName(left.drugName).localeCompare(formatDrugName(right.drugName), undefined, {
            sensitivity: 'base',
          })
        ),
    [drugs]
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return allRows;

    return allRows.filter((row) => row.drugName?.toLowerCase().includes(term));
  }, [allRows, search]);

  const totalItemsText = `${allRows.length} ${allRows.length === 1 ? 'item' : 'items'}`;
  const itemCountText = search.trim()
    ? `${rows.length} of ${allRows.length} items`
    : totalItemsText;

  const selectedItem = useMemo(
    () => allRows.find((row) => row.catalogId === selectedCatalogId) ?? null,
    [allRows, selectedCatalogId]
  );

  const handleToggleStatus = () => {
    const variantDescription = describeRow(selectedItem);

    showInfoDialog({
      title: `Update ${selectedItem.drugName} Catalog Status`,
      message: `Please confirm to delete ${selectedItem.drugName}${
        variantDescription ? ` - ${variantDescription}` : ''
      } from this partner?`,
      checkbox: ALERT_PARTNER_CHECKBOX,
      callback: (alertPartners) =>
        updatePartnerCatalogApi({
          partnerUuid: partner.partnerUuid,
          partnerCatalog: [{ catalogId: selectedItem.catalogId, isActive: !selectedItem.isActive }],
          alertPartners,
        })
          .then(() => {
            enqueueSnackbar('Catalog updated successfully', { variant: 'success' });
            return loadPartnerCatalog();
          })
          .catch((error) => dispatch(handleError({ error }))),
      isWarning: true,
      cancelable: true,
      confirmButtonText: 'Confirm',
    });
  };

  const handleDialogClose = (_, reason) => {
    if (reason === 'backdropClick') return;
    handleClose();
  };

  return (
    <Dialog open={showModal} onClose={handleDialogClose} maxWidth='lg' fullWidth>
      <InfoDialog />

      <CatalogDialogHeader
        title={`Manage Catalog - ${capitalizeName(partner.partnerName)}`}
        onClose={handleDialogClose}
      />

      <DialogContent sx={{ p: 3, height: '70vh' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              size='small'
              sx={{ minWidth: 320 }}
              placeholder='Search catalog by drug name'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
              }}
            />
            {!loading && (
              <Typography variant='body2' color='text.secondary'>
                {itemCountText}
              </Typography>
            )}
          </Box>
          <Button variant='contained' onClick={() => setAddOpen(true)}>
            Add
          </Button>
        </Box>

        <Grid container spacing={2} sx={{ height: 'calc(100% - 56px)' }}>
          <Grid item xs={12} sm={5} sx={{ height: '100%' }}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {loading ? (
              <LoadingModule height='30vh' />
            ) : isEmpty(rows) ? (
              <NothingFound height='30vh' customMessage='No catalog items found' />
            ) : (
              <Paper variant='outlined' sx={{ height: '100%', overflow: 'auto' }}>
                <List disablePadding dense>
                  {rows.map((row) => (
                    <ListItem key={row.catalogId} disableGutters>
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          bgcolor:
                            row.catalogId === selectedCatalogId
                              ? lighten(theme.palette.primary.main, 0.9)
                              : 'inherit',
                        }}
                        onClick={() => setSelectedCatalogId(row.catalogId)}
                      >
                        <ListItemText primary={row.drugName} secondary={describeRow(row)} />
                        {row.isAvailable ? (
                          <CheckIcon color='success' />
                        ) : (
                          <CloseIcon color='error' />
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} sm={7} sx={{ height: '100%' }}>
            {!selectedItem ? (
              <Paper
                variant='outlined'
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                }}
              >
                <Typography variant='body1' color='text.secondary'>
                  Select an active catalog to start
                </Typography>
              </Paper>
            ) : (
              <Paper variant='outlined' sx={{ height: '100%', p: 3, overflow: 'auto' }}>
                <Grid container justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
                  <Typography variant='h6' color='primary'>
                    Catalog Details
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant='contained' onClick={() => setEditOpen(true)}>
                      Edit
                    </Button>
                    <Button variant='contained' color='error' onClick={handleToggleStatus}>
                      Delete
                    </Button>
                  </Box>
                </Grid>

                <Grid container direction='column' rowSpacing={2}>
                  <Grid item>
                    <Typography variant='body2' color='primary'>
                      Drug Name
                    </Typography>
                    <Typography variant='body1'>{selectedItem.drugName}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant='body2' color='primary'>
                      Drug Strength
                    </Typography>
                    <Typography variant='body1'>{selectedItem.strength || 'N/A'}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant='body2' color='primary'>
                      Pack Size
                    </Typography>
                    <Typography variant='body1'>
                      {formatPackSize(selectedItem.packSizeValue, selectedItem.packSizeUnit)}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant='body2' color='primary'>
                      Form
                    </Typography>
                    <Typography variant='body1'>
                      {formatForm(selectedItem.formAlias, selectedItem.dosageForm)}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant='body2' color='primary'>
                      Product Id
                    </Typography>
                    <Typography variant='body1'>{selectedItem.productId || '-'}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant='body2' color='primary'>
                      Is Available
                    </Typography>
                    <Typography variant='body1'>
                      {selectedItem.isAvailable ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant='body2' color='primary'>
                      Sigs
                    </Typography>
                    {isEmpty(selectedItem.sigs) ? (
                      <Typography variant='body1'>No sigs for this drug</Typography>
                    ) : (
                      selectedItem.sigs.map((sig) => (
                        <Typography variant='body2' key={sig}>
                          {sig}
                        </Typography>
                      ))
                    )}
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' onClick={handleClose}>
          Close
        </Button>
      </DialogActions>

      <EditPartnerCatalogItemDialog
        open={editOpen}
        item={selectedItem}
        partnerUuid={partner.partnerUuid}
        onClose={() => setEditOpen(false)}
        onSaved={loadPartnerCatalog}
      />

      <AddPartnerCatalogItemDialog
        open={addOpen}
        partner={partner}
        onClose={() => setAddOpen(false)}
        onSaved={loadPartnerCatalog}
      />
    </Dialog>
  );
};

PartnerCatalogModal.propTypes = {
  partner: PropTypes.shape({
    partnerUuid: PropTypes.string.isRequired,
    partnerName: PropTypes.string.isRequired,
  }).isRequired,
  showModal: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default PartnerCatalogModal;
